import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Shield } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface TwoFactorEnableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  userId: string;
  email: string;
  userName: string;
}

export function TwoFactorEnableDialog({
  open,
  onOpenChange,
  onSuccess,
  userId,
  email,
  userName,
}: TwoFactorEnableDialogProps) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  const sendCode = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke("send-2fa-code", {
        body: { userId, email, userName },
      });

      if (error) throw error;

      setCodeSent(true);
      toast.success("Código enviado para seu e-mail!");
    } catch (error: any) {
      toast.error("Erro ao enviar código: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast.error("O código deve ter 6 dígitos");
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "verify-2fa-code",
        {
          body: { userId, code },
        }
      );

      if (error) throw error;

      if (data?.success) {
        // Ativar 2FA nas configurações
        const { error: updateError } = await supabase
          .from("user_settings")
          .update({ two_factor_enabled: true })
          .eq("user_id", userId);

        if (updateError) throw updateError;

        toast.success("Autenticação de dois fatores ativada com sucesso!");
        onSuccess();
        onOpenChange(false);
        setCode("");
        setCodeSent(false);
      } else {
        throw new Error(data?.error || "Código inválido");
      }
    } catch (error: any) {
      toast.error("Erro na verificação: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setCode("");
    setCodeSent(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <DialogTitle>Ativar Autenticação de Dois Fatores</DialogTitle>
          </div>
          <DialogDescription>
            {!codeSent
              ? "Enviaremos um código de verificação para seu e-mail"
              : "Digite o código de 6 dígitos enviado para " + email}
          </DialogDescription>
        </DialogHeader>

        {!codeSent ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Para ativar a autenticação de dois fatores, primeiro precisamos verificar
              seu e-mail. Clique no botão abaixo para receber o código.
            </p>
            <Button onClick={sendCode} disabled={loading} className="w-full">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                "Enviar Código"
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">Código de Verificação</Label>
              <Input
                id="code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="text-center text-2xl tracking-widest font-mono"
                autoFocus
              />
              <p className="text-xs text-muted-foreground text-center">
                O código expira em 10 minutos
              </p>
            </div>

            <DialogFooter className="flex-col sm:flex-col gap-2">
              <Button
                onClick={handleVerify}
                disabled={loading || code.length !== 6}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  "Ativar 2FA"
                )}
              </Button>
              <Button
                variant="outline"
                onClick={sendCode}
                disabled={loading}
                className="w-full"
              >
                Reenviar Código
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
